import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ContactWidget from '../../components/ui/ContactWidget';
import ServiceCard from './components/ServiceCard';
import ProcessTimeline from './components/ProcessTimeline';
import QuoteForm from './components/QuoteForm';
import ClientLogos from './components/ClientLogos';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon'; // Assuming Icon component exists

// --- NEW SEO-FOCUSED FAQ COMPONENT ---
const FaqSection = () => {
  const faqData = [
    {
      question: 'What types of events do you specialize in in Nairobi?',
      answer: 'We specialize in a wide range of events in Nairobi and across Kenya, including luxury weddings, corporate functions, product launches, VIP socials, and intimate private celebrations. Our core focus is on providing bespoke event decor and styling services.'
    },
    {
      question: 'How much does professional event decor cost in Kenya?',
      answer: 'The cost of event decor in Kenya varies significantly based on the event scale, guest count, and complexity of the design. Our customizable packages for Event Decor & Styling start from KES 150,000. We provide a detailed quote after our initial consultation to match your specific budget and vision.'
    },
    {
      question: 'Do you provide services outside of Nairobi?',
      answer: 'Absolutely. While we are a leading event company based in Nairobi, we have extensive experience planning and executing flawless events in locations all over Kenya, including Naivasha, Mombasa, Nanyuki, and the Maasai Mara.'
    },
    {
      question: 'How far in advance should I book your event planning services?',
      answer: 'For large-scale events like weddings or corporate galas, we recommend booking 6-12 months in advance to ensure availability and ample time for planning. For smaller events, 2-3 months is typically sufficient. Contact us to discuss your timeline.'
    }
  ];

  return (
    <div className="bg-white rounded-xl p-8 lg:p-12">
      <h2 className="font-heading font-bold text-3xl text-center text-foreground mb-8">Frequently Asked Questions</h2>
      <div className="space-y-6 max-w-4xl mx-auto">
        {faqData.map((faq, index) => (
          <div key={index} className="border-b pb-4">
            <h3 className="font-semibold text-lg text-foreground">{faq.question}</h3>
            <p className="text-muted-foreground mt-2">{faq.answer}</p>
          </div>
        ))}
      </div>
    </div>
  );
};


const Services = () => {
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  // SEO Optimized Service Details
  const services = [
    {
      id: 'event-decor',
      title: 'Event Decor & Styling in Nairobi',
      subtitle: 'Award-Winning Bespoke Event Design in Kenya',
      description: `As premier event stylists in Nairobi, we are visual storytellers. Our passion is to transform any venue into an immersive environment that captivates guests and creates an unforgettable atmosphere for weddings, corporate events, and private parties across Kenya.`,
      image: '/gallery/wedding.jpg', 
      features: [
        'Full Event & Theme Conceptualization',
        'Bespoke Floral Design & Installation in Nairobi',
        'Ambient Lighting & A/V Production',
        'Luxury Furniture & Linen Rentals',
        'Custom Backdrops & Stage Design',
        'Detailed Table Scape & Centerpiece Design',
        'Venue Floor Plan & Guest Flow Optimization',
        'On-site Styling & Flawless Execution'
      ],
    
      testimonial: {
        text: 'The decor was more beautiful than I ever imagined. The wedding looked like an event you\'d expect from prominent people and celebrities.',
        client: 'Krystal & Eric, Naivasha Wedding'
      }
    },
    {
      id: 'corporate-events',
      title: 'Corporate Event Planners in Kenya',
      subtitle: 'Professional Business Events with Lasting Impact',
      description: `Elevate your corporate image with professionally managed events in Nairobi and beyond. We create engaging experiences for conferences, product launches, and galas that align perfectly with your brand's objectives and deliver tangible results.`,
      image: '/gallery/event.jpg',
      features: [
        'Conference & Seminar Planning', 'Product Launch Management', 'Team Building Events in Kenya',
        'Corporate Gala Dinners & Awards Ceremonies', 'Trade Show & Exhibition Management',
        'Executive Retreats', 'Brand Activations', 'End-of-Year Parties'
      ],
      
      testimonial: {
        text: 'Your contribution played a key role in making the event a success, and we truly couldn’t have done it without you. The launcher was definitely the highlight of the event!',
        client: 'World Resource Institute, Nairobi'
      }
    },
    {
      id: 'private-celebrations',
      title: 'Private Party & Celebration Planning',
      subtitle: 'Intimate Gatherings for Life\'s Special Moments',
      description: `Create lasting memories with personalized celebrations tailored to your unique style. From milestone birthdays in Nairobi to anniversaries in the Mara, we bring your vision to life with elegance, discretion, and meticulous attention to detail.`,
      image: '/gallery/private.jpg',
      features: [
        'Milestone Birthday & Anniversary Parties', 'Family Reunion Coordination', 'Luxury Graduation Celebrations',
        'Themed Holiday Parties', 'Retirement Celebrations', 'Elegant Baby Showers & Gender Reveals',
        'Bespoke Engagement Parties', 'Intimate Memorial Services'
      ],
      testimonial: {
        text: 'You went above and beyond, and your team was amazing. We truly appreciate it. Thank you again.',
        client: 'Faith (Birthday Celebration, Nairobi)'
      }
    },
    {
      id: 'event-logistics',
      title: 'Premium Rentals & Event Logistics',
      subtitle: 'The Foundational Elements for a Flawless Event',
      description: `Our expertise extends to the complex logistics that underpin a successful event. We manage sourcing for luxury furniture, custom tents, and sophisticated A/V and lighting productions for events of any scale in Kenya.`,
      image: '/gallery/tent.jpg',
      features: [
        'Luxury Tent Rentals (High-Peak & Marquee)', 'Premium Furniture & Decor Sourcing', 'Sophisticated Lighting & A/V Production',
        'On-site Power & Utilities Management', 'Staging, Flooring & Dance Floor Solutions', 'Comprehensive Load-in/Load-out Schedules',
        'Technical Crew & Staff Coordination', 'Contingency, Weather & Safety Planning'
      ],
      
      testimonial: {
        text: 'The delivery was impeccable. Tulienjoy hiyo decor. I will do a proper message nikirecover.',
        client: 'Wangari & Kiambi, Runda Wedding'
      }
    },
    {
      id: 'vip-socials',
      title: 'VIP Socials & Lifestyle Events',
      subtitle: 'Curated Experiences for Luxury Brands & Private Clients',
      description: `We design and execute exclusive, high-profile social events that build brand prestige and create unforgettable moments. From influencer gatherings to large-scale charity galas, our focus is on creating a sophisticated and impactful experience.`,
      image: '/gallery/vip.jpg',
      features: [
        'Exclusive Product Launch Parties', 'Curated Influencer & Media Events', 'Luxury Brand Activations & Pop-ups',
        'High-Profile Charity Galas & Fundraisers', 'Private Art Gallery Openings', 'Exclusive Client Appreciation Dinners',
        'Full Guest Management & Concierge Services', 'Press & Media Relations Coordination'
      ],
      testimonial: {
        text: 'You and your team always deliver, and your level of professionalism and execution is top-notch. I appreciate you as well and definitely choosing you again.',
        client: 'Asha (Family House Warming, Karen)'
      }
    }
  ];

  const processSteps = [
    { title: 'Initial Consultation', description: 'A detailed discussion about your vision, budget, and requirements for your event in Nairobi or your chosen location.', icon: 'MessageSquare', duration: '1-2 hours', details: ['Vision and theme discussion', 'Budget planning and allocation', 'Timeline establishment', 'Initial vendor recommendations'] },
    { title: 'Planning & Design', description: 'Our team creates a comprehensive event plan with detailed timelines, vendor selections, and bespoke design concepts.', icon: 'PenTool', duration: '2-4 weeks', details: ['Detailed event timeline creation', 'Vendor research and selection', 'Design concept development', 'Contract negotiations'] },
    { title: 'Coordination & Setup', description: 'We handle all logistics, vendor coordination, and on-site setup to ensure everything is perfect for your event.', icon: 'Settings', duration: '1-2 days', details: ['Vendor coordination and management', 'Setup supervision and quality control', 'Final walkthrough and adjustments', 'Emergency contingency activation'] },
    { title: 'Event Execution', description: 'On the event day, we manage every detail flawlessly so you can relax and immerse yourself in the celebration.', icon: 'Star', duration: 'Event day', details: ['Real-time event management', 'Guest assistance and coordination', 'Vendor supervision and troubleshooting', 'Timeline adherence and adjustments'] }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Services', icon: 'Grid3X3' },
    { value: 'decor', label: 'Decor & Styling', icon: 'Palette' },
    { value: 'corporate', label: 'Corporate', icon: 'Briefcase' },
    { value: 'private', label: 'Private', icon: 'Users' },
    { value: 'logistics-vip', label: 'Logistics & VIP', icon: 'Star' }
  ];

  const filteredServices = services.filter(service => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'decor') return service.id === 'event-decor';
    if (activeFilter === 'corporate') return service.id === 'corporate-events';
    if (activeFilter === 'private') return service.id === 'private-celebrations';
    if (activeFilter === 'logistics-vip') return service.id === 'event-logistics' || service.id === 'vip-socials';
    return true;
  });

  const handleGetQuote = (service) => {
    setSelectedService(service);
    setShowQuoteForm(true);
  };

  const handleQuoteSubmit = (formData) => {
    console.log('Quote request submitted:', formData);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // --- STRUCTURED DATA FOR LOCAL SEO & FAQ ---
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Luxe & Allure Events Company",
    "image": "https://www.luxeandallureevents.co.ke/logo.png", // Replace with your actual logo URL
    "@id": "https://www.luxeandallureevents.co.ke/", // Replace with your website URL
    "url": "https://www.luxeandallureevents.co.ke/services", // Replace with your services page URL
    "telephone": "+254727937810",
    "priceRange": "KES",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "16 Nairobi, Muthaiga North, Garden Estate Rd", // Add your physical address if available
      "addressLocality": "Nairobi",
      "postalCode": "00100",
      "addressCountry": "KE"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -1.2336981203295856, // Replace with your actual coordinates
      "longitude": 36.8646807701427 // Replace with your actual coordinates
    },
    "description": "Premier event decor and planning company in Nairobi, Kenya, specializing in luxury weddings, corporate events, and bespoke celebrations.",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday"
      ],
      "opens": "09:00",
      "closes": "17:00"
    },
    "servesCuisine": "", // Not applicable
    "areaServed": {
      "@type": "AdministrativeArea",
      "name": "Kenya"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What types of events do you specialize in in Nairobi?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We specialize in a wide range of events in Nairobi and across Kenya, including luxury weddings, corporate functions, product launches, VIP socials, and intimate private celebrations. Our core focus is on providing bespoke event decor and styling services."
        }
      },
      {
        "@type": "Question",
        "name": "How much does professional event decor cost in Kenya?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The cost of event decor in Kenya varies significantly based on the event scale, guest count, and complexity of the design. Our customizable packages for Event Decor & Styling start from KES 150,000. We provide a detailed quote after our initial consultation to match your specific budget and vision."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide services outside of Nairobi?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. While we are a leading event company based in Nairobi, we have extensive experience planning and executing flawless events in locations all over Kenya, including Naivasha, Mombasa, Nanyuki, and the Maasai Mara."
        }
      }
    ]
  };


  return (
    <>
      <Helmet>
        <title>Expert Event Decor & Planning Services in Nairobi - Luxe & Allure</title>
        <meta name="description" content="Luxe & Allure is a top-rated event decor and planning company in Nairobi, Kenya. We offer bespoke decor, corporate event management, and luxury wedding planning. Get a free quote today!" />
        <meta name="keywords" content="event decor Nairobi, event planners Kenya, wedding planners Nairobi, corporate events Kenya, luxury event decor, event company Nairobi, party planners Kenya, decor companies in Nairobi" />
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <Header />
      <ContactWidget />

      <main className="min-h-screen bg-background pt-20">
        <div className="container mx-auto px-6 lg:px-8 pt-12 pb-32">
          <Breadcrumb />

          <div className="text-center mb-16">
            <h1 className="font-heading font-bold text-4xl lg:text-5xl text-foreground mb-6">
              Our Premium Event Services in Nairobi
            </h1>
            <p className="font-body text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
              From immersive decor that tells a story to flawlessly executed corporate events, we are Nairobi's premier choice for creating unforgettable experiences. Explore our services to see how we bring visions to life across Kenya.
            </p>
            
            <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
              {filterOptions.map((option) => (
                <Button
                  key={option.value}
                  variant={activeFilter === option.value ? 'default' : 'outline'}
                  onClick={() => setActiveFilter(option.value)}
                  className="transition-smooth"
                >
                  <Icon name={option.icon} className="mr-2 h-4 w-4" />
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-24">
            {filteredServices.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                onGetQuote={handleGetQuote}
              />
            ))}
          </div>

          <div className="mb-24">
            <ProcessTimeline
              steps={processSteps}
              title="Our Proven Event Planning Process"
            />
          </div>
          
          {/* --- NEW FAQ SECTION RENDERED HERE --- */}
          <div className="mb-24">
            <FaqSection />
          </div>

          <div className="mb-24">
            <ClientLogos />
          </div>

          <div className="bg-primary rounded-xl p-8 lg:p-12 text-center">
            <h2 className="font-heading font-bold text-3xl text-white mb-4">
              Ready to Plan Your Event in Nairobi?
            </h2>
            <p className="font-body text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Let's discuss your vision and create an unforgettable experience together. Contact the leading event planners in Kenya today for a personalized consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="secondary"
                size="lg"
                onClick={() => setShowQuoteForm(true)}
                className="glassmorphic"
              >
                <Icon name="MessageSquare" className="mr-2 h-5 w-5" />
                Get a Free Quote
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => window.location.href = 'tel:0727937010'}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <Icon name="Phone" className="mr-2 h-5 w-5" />
                Call (072) 793-7010
              </Button>
            </div>
          </div>
        </div>
      </main>

      {showQuoteForm && (
        <QuoteForm
          selectedService={selectedService}
          onClose={() => {
            setShowQuoteForm(false);
            setSelectedService(null);
          }}
          onSubmit={handleQuoteSubmit}
          services={services}
        />
      )}
    </>
  );
};

export default Services;